import { Box, Text } from '@factorialco/deprecated-design-system'
import { PlanningBalancesQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'

import HoursDisplay, { Element, formatDuration } from './HoursDisplay'

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]

interface Props {
  balances: TimePlanningBalance
}

const HoursToPlan = ({ balances }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isUnpaidLeaveCapEnabled = hasFeatureFlag(
    'dev_time_tracking_agreement_hours_unpaid_leaves_cap'
  )

  const formatHours = (hours: number | null | undefined) => formatDuration((hours ?? 0) * 3600000)
  const formatHoursWithMinus = (hours: number) => {
    return hours === 0 ? formatHours(hours) : `- ${formatHours(hours)}`
  }

  const { contractHours = 0, leaveHours = 0, bankHolidayHours = 0 } = balances

  // contractHours comes from backend with bankHolidayHours already deducted.
  //
  // FF disabled:
  // - hoursToPlan = contractHours
  // - totalHours (gross agreement) = contractHours + leaveHours + bankHolidayHours
  //
  // FF enabled:
  // - hoursToPlan = contractHours - leaveHours
  // - totalHours (gross agreement) = contractHours + bankHolidayHours
  //
  // Round to 2 decimals to avoid floating-point artifacts.
  const hoursToPlan = isUnpaidLeaveCapEnabled
    ? Number(((contractHours ?? 0) - (leaveHours ?? 0)).toFixed(2))
    : Number((contractHours ?? 0).toFixed(2))

  const totalHours = isUnpaidLeaveCapEnabled
    ? Number(((contractHours ?? 0) + (bankHolidayHours ?? 0)).toFixed(2))
    : Number(((contractHours ?? 0) + (leaveHours ?? 0) + (bankHolidayHours ?? 0)).toFixed(2))

  const formattedTotalHours = formatHours(totalHours)
  const formattedLeaveHours = formatHoursWithMinus(leaveHours ?? 0)
  const formattedBankHolidayHours = formatHoursWithMinus(bankHolidayHours ?? 0)
  const formattedHoursToPlan = formatHours(hoursToPlan ?? 0)

  return (
    <HoursDisplay
      title={i18n.t('shift_management.hours_information.hours_to_plan')}
      totalDuration={formattedHoursToPlan}
      triggerColor="grey800"
    >
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text size="100" weight="medium" color="grey1000">
          {i18n.t('shift_management.hours_information.agreement_hours')}
        </Text>
        <Text size="100" weight="medium" color="grey1000">
          {formattedTotalHours}
        </Text>
      </Box>

      <Element
        title={i18n.t('shift_management.hours_information.approved_leaves')}
        value={formattedLeaveHours}
      />
      <Element
        title={i18n.t('shift_management.hours_information.bank_holidays')}
        value={formattedBankHolidayHours}
      />
    </HoursDisplay>
  )
}

export default HoursToPlan
